import { useCallback, useState } from 'react';
import { isSessionExpiredError } from '../../../api';
import {
  executeRagQuery,
  findSourceIndex,
  getPdfUrlString,
  isNoInformationAnswer,
  makeEmptyTurn,
  normalizeDocHint,
  normalizeSerffId,
  resolveCitation,
} from '../answersService';

const normalizeCitation = (citation) => ({
  ...citation,
  serffId: normalizeSerffId(citation.serffId),
  docHint: normalizeDocHint(citation.docHint) || undefined,
});

export function useAnswersRagSession(buildFilterPayload) {
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState('');

  const updateTurn = useCallback((id, updater) => {
    setConversation((turns) => turns.map((turn) => (turn.id === id ? updater({ ...turn }) : turn)));
  }, []);

  const submitQuery = useCallback(async (questionOverride) => {
    const question = (questionOverride || newMessage || '').trim();
    if (!question || loading) return;
    setNewMessage('');
    setLoading(true);
    setError('');
    setSelectedSource(null);
    setPdfLoadError('');

    const turn = makeEmptyTurn(question);
    setConversation((prev) => [...prev, turn]);

    try {
      await executeRagQuery({
        question,
        conversation,
        buildFilterPayload,
        updateTurn,
        turnId: turn.id,
      });
    } catch (err) {
      if (isSessionExpiredError(err)) return;
      setError(err?.message || 'Request failed');
      setConversation((prev) => prev.filter((t) => t.id !== turn.id));
    } finally {
      setLoading(false);
    }
  }, [newMessage, loading, conversation, buildFilterPayload, updateTurn]);

  const referencesForTurn = useCallback(
    (turn) => (isNoInformationAnswer(turn) ? [] : (turn.response?.sources || [])),
    [],
  );

  const findSourceForCitation = useCallback((turn, citation) => {
    const sources = turn.response?.sources || [];
    if (citation.sourceIndex != null && citation.sourceIndex >= 0 && citation.sourceIndex < sources.length) {
      return sources[citation.sourceIndex];
    }
    const idx = findSourceIndex(sources, citation.serffId, citation.pageStart, citation.pageEnd, citation.docHint);
    return idx >= 0 ? sources[idx] : null;
  }, []);

  const handleCitationClick = useCallback(async (turn, rawCitation) => {
    const citation = normalizeCitation(rawCitation);
    setPdfLoadError('');

    const sources = turn.response?.sources || [];
    if (
      citation.sourceIndex != null
      && citation.sourceIndex >= 0
      && citation.sourceIndex < sources.length
    ) {
      const fromIndex = sources[citation.sourceIndex];
      if (getPdfUrlString(fromIndex)) {
        setSelectedSource({
          ...fromIndex,
          page_start: citation.pageStart,
          page_end: citation.pageEnd,
        });
        return;
      }
    }

    const applyResolved = (base, resolved) => ({
      ...base,
      ...resolved,
      serff_id: normalizeSerffId(resolved?.serff_id || base.serff_id),
      page_start: citation.pageStart,
      page_end: citation.pageEnd,
    });

    const tryResolve = async (base) => {
      const resolved = await resolveCitation({
        serff_id: citation.serffId,
        page_start: citation.pageStart,
        page_end: citation.pageEnd,
        file_name_hint: citation.docHint,
      });
      if (resolved) {
        setSelectedSource(applyResolved(base, resolved));
        return true;
      }
      return false;
    };

    const existing = findSourceForCitation(turn, citation);
    if (existing) {
      if (getPdfUrlString(existing)) {
        setSelectedSource(existing);
        return;
      }
      const base = {
        ...existing,
        serff_id: citation.serffId,
        page_start: citation.pageStart,
        page_end: citation.pageEnd,
      };
      setSelectedSource(base);
      const ok = await tryResolve(base);
      if (!ok) {
        setPdfLoadError(
          'This citation could not be linked to a document. The page may not be indexed yet, or the filing is outside your scope.',
        );
      }
      return;
    }

    const fallback = {
      serff_id: citation.serffId,
      product_name: citation.docHint || citation.serffId,
      page_start: citation.pageStart,
      page_end: citation.pageEnd,
      text: '',
      file_name: '',
      file_path: '',
    };
    setSelectedSource(fallback);
    const ok = await tryResolve(fallback);
    if (!ok) {
      setPdfLoadError(
        'This citation could not be linked to a document. The page may not be indexed yet, or the filing is outside your scope.',
      );
    }
  }, [findSourceForCitation]);

  const selectedPdfUrl = selectedSource ? getPdfUrlString(selectedSource) : null;

  const handlePdfLoadError = useCallback((message) => {
    setPdfLoadError(message || 'This PDF could not be loaded. It may be unavailable or you may not have access.');
  }, []);

  const handlePdfLoadSuccess = useCallback(() => {
    setPdfLoadError('');
  }, []);

  const resetSession = useCallback(() => {
    setConversation([]);
    setLoading(false);
    setError('');
    setNewMessage('');
    setSelectedSource(null);
    setPdfLoadError('');
  }, []);

  return {
    conversation,
    error,
    handleCitationClick,
    handlePdfLoadError,
    handlePdfLoadSuccess,
    loading,
    newMessage,
    pdfLoadError,
    referencesForTurn,
    resetSession,
    selectedPdfUrl,
    selectedSource,
    setConversation,
    setError,
    setNewMessage,
    setPdfLoadError,
    setSelectedSource,
    submitQuery,
    updateTurn,
  };
}
